import React, { useState, useEffect, useRef } from 'react';
import { AiOutlineFileText } from 'react-icons/ai';
import { apiUrl } from '../config/api';
import './ProjectNotes.css';

// Notes about the project as a whole (access, what was found, follow-ups) — not the
// per-photo notes. Each note keeps its author and time; only the author can edit it,
// and the author, an admin or a manager can delete it (the server enforces both).
const MAX_LENGTH = 5000;

const timeAgo = (iso) => {
  const then = new Date(iso);
  const secs = Math.round((Date.now() - then.getTime()) / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days} d ago`;
  return then.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
};

const ProjectNotes = ({ clientId, token }) => {
  const [notes, setNotes] = useState([]);
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const requestRef = useRef(0);
  const listRef = useRef(null);

  const call = async (path, options = {}) => {
    const res = await fetch(apiUrl(path), {
      ...options,
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', ...(options.headers || {}) },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.detail || data.message || 'Something went wrong');
    return data;
  };

  useEffect(() => {
    const id = ++requestRef.current;
    setStatus('loading');
    setNotes([]);
    setDraft('');
    setEditingId(null);
    setError('');
    call(`/api/scraper/clients/${clientId}/notes`)
      .then((data) => { if (id === requestRef.current) { setNotes(data.notes || []); setStatus('ready'); } })
      .catch(() => { if (id === requestRef.current) setStatus('error'); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const handleAdd = async () => {
    const body = draft.trim();
    if (!body || saving) return;
    setSaving(true);
    setError('');
    try {
      const { note } = await call(`/api/scraper/clients/${clientId}/notes`, {
        method: 'POST',
        body: JSON.stringify({ body }),
      });
      setNotes((prev) => [note, ...prev]);
      setDraft('');
      listRef.current?.scrollTo({ top: 0 });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async (noteId) => {
    const body = editText.trim();
    if (!body || saving) return;
    setSaving(true);
    setError('');
    try {
      const { note } = await call(`/api/scraper/project-notes/${noteId}`, {
        method: 'PATCH',
        body: JSON.stringify({ body }),
      });
      setNotes((prev) => prev.map((n) => (n.id === noteId ? note : n)));
      setEditingId(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    setError('');
    try {
      await call(`/api/scraper/project-notes/${noteId}`, { method: 'DELETE' });
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
    } catch (err) {
      setError(err.message);
    }
  };

  // Ctrl/Cmd+Enter saves, so a note can be written without reaching for the mouse.
  const submitOnShortcut = (e, action) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); action(); }
  };

  return (
    <section className="pn" aria-labelledby="pn-title">
      <div className="pn-header">
        <h2 id="pn-title" className="pn-title">
          <AiOutlineFileText size={16} /> Project notes
          {notes.length > 0 && <span className="pn-count">{notes.length}</span>}
        </h2>
      </div>

      <div className="pn-compose">
        <textarea
          className="pn-input pn-input--compose"
          placeholder="Add a note — access, what you found, follow-ups…"
          value={draft}
          maxLength={MAX_LENGTH}
          // One line until there's something in it, so the notes keep the room.
          rows={draft.includes('\n') || draft.length > 60 ? 3 : 1}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => submitOnShortcut(e, handleAdd)}
          aria-label="New project note"
        />
        <button type="button" className="pn-btn pn-btn--primary" onClick={handleAdd} disabled={!draft.trim() || saving}>
          {saving && !editingId ? 'Saving…' : 'Add'}
        </button>
      </div>
      {draft.length > MAX_LENGTH - 500 && (
        <span className="pn-limit">{MAX_LENGTH - draft.length} characters left</span>
      )}

      {error && <div className="pn-error" role="alert">{error}</div>}

      {status === 'loading' && <div className="pn-empty">Loading notes…</div>}
      {status === 'error' && <div className="pn-empty">Couldn&rsquo;t load notes. Refresh to try again.</div>}
      {status === 'ready' && notes.length === 0 && (
        <div className="pn-empty">No notes yet.</div>
      )}

      {notes.length > 0 && (
        // Newest first; older notes scroll inside the panel.
        <ul ref={listRef} className="pn-list" aria-label="Project notes, newest first">
          {notes.map((note) => (
            <li key={note.id} className="pn-note">
              <div className="pn-meta">
                <span className="pn-author">{note.authorName}</span>
                <span className="pn-time" title={new Date(note.createdAt).toLocaleString()}>
                  {timeAgo(note.createdAt)}
                </span>
                {note.edited && (
                  <span className="pn-edited" title={`Edited ${new Date(note.updatedAt).toLocaleString()}`}>edited</span>
                )}
                {editingId !== note.id && (note.canEdit || note.canDelete) && (
                  <span className="pn-actions">
                    {note.canEdit && (
                      <button type="button" className="pn-link"
                        onClick={() => { setEditingId(note.id); setEditText(note.body); }}>
                        Edit
                      </button>
                    )}
                    {note.canDelete && (
                      <button type="button" className="pn-link pn-link--danger" onClick={() => handleDelete(note.id)}>
                        Delete
                      </button>
                    )}
                  </span>
                )}
              </div>
              {editingId === note.id ? (
                <div className="pn-edit">
                  <textarea
                    className="pn-input"
                    value={editText}
                    maxLength={MAX_LENGTH}
                    rows={3}
                    autoFocus
                    onChange={(e) => setEditText(e.target.value)}
                    onKeyDown={(e) => submitOnShortcut(e, () => handleSaveEdit(note.id))}
                    aria-label="Edit note"
                  />
                  <div className="pn-compose-row">
                    <button type="button" className="pn-btn" onClick={() => setEditingId(null)} disabled={saving}>
                      Cancel
                    </button>
                    <button type="button" className="pn-btn pn-btn--primary"
                      onClick={() => handleSaveEdit(note.id)} disabled={!editText.trim() || saving}>
                      {saving ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                </div>
              ) : (
                <p className="pn-body">{note.body}</p>
              )}
            </li>
          ))}
        </ul>
      )}

    </section>
  );
};

export default ProjectNotes;
